import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Play, Trophy, Clock, User, XCircle, Volume2, VolumeX } from 'lucide-react';

const initialObjectsData = [
  { id: 1, name: "Sunflowers", count: 8, emoji: "🌻" },
  { id: 2, name: "Mugs", count: 5, emoji: "☕" },
  { id: 3, name: "Sandwiches", count: 7, emoji: "🥪" },
  { id: 4, name: "Ice Creams", count: 9, emoji: "🍦" },
  { id: 5, name: "Fish", count: 10, emoji: "🐟" }
];

const baseMatchNumbers = [5, 8, 9, 7, 10];

// Helper to shuffle arrays
const shuffleArray = (array) => {
  return [...array].sort(() => Math.random() - 0.5);
};

export default function CountAndMatchGame() {
  const [playerName, setPlayerName] = useState('');
  const [activePlayer, setActivePlayer] = useState('Little Learner');
  const [isMuted, setIsMuted] = useState(false);
  
  // Game states
  const [objectsList, setObjectsList] = useState(() => shuffleArray(initialObjectsData));
  const [numberOptions, setNumberOptions] = useState(() => shuffleArray(baseMatchNumbers));
  
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timer, setTimer] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [gameResult, setGameResult] = useState({ ok: false, message: '' });

  // Web Speech API Voice synthesis helper
  const speakText = (text) => {
    if (isMuted) return; // Respect mute state
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Stop any ongoing speech
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.1;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Keep activePlayer in sync when input changes directly
  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setPlayerName(value);
    setActivePlayer(value.trim() ? value.trim() : 'Little Learner');
  };

  // Toggle voice mute state
  const toggleMute = () => {
    if (!isMuted && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsMuted(!isMuted);
  };

  // Announce instructions on initial render
  useEffect(() => {
    speakText("Welcome! Please enter your name, then count the objects and select the correct numbers.");
  }, []);

  // Timer Effect
  useEffect(() => {
    let interval = null;
    if (isTimerActive) {
      interval = setInterval(() => {
        setTimer((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerActive]);

  const handleSelectNumber = (objectId, number) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [objectId]: number
    }));
  };

  const handleResetCurrent = () => {
    setSelectedAnswers({});
    setTimer(0);
    setIsTimerActive(true);
    speakText("Choices cleared. Try again!");
  };

  const handlePlayAgainAndShuffle = () => {
    const currentName = playerName.trim() || 'Little Learner';

    setActivePlayer(currentName);
    
    // Shuffle rows and options
    setObjectsList(shuffleArray(initialObjectsData));
    setNumberOptions(shuffleArray(baseMatchNumbers));
    
    // Reset state
    setSelectedAnswers({});
    setTimer(0);
    setIsTimerActive(true);

    // Read voice welcome
    speakText(`Welcome ${currentName}! Count the objects and select the correct number.`);
  };

  const handleSubmit = () => {
    setIsTimerActive(false);

    // Verify answers
    const isAllCorrect = objectsList.every(
      (obj) => selectedAnswers[obj.id] === obj.count
    );

    const resultMsg = isAllCorrect
      ? `Outstanding, ${activePlayer}! You matched all the numbers correctly!`
      : `Some numbers don't match yet, ${activePlayer}. Try again!`;

    setGameResult({ ok: isAllCorrect, message: resultMsg });
    setShowModal(true);

    // Voice announcement of result
    speakText(resultMsg);
  };

  const closeModal = () => {
    setShowModal(false);
    handlePlayAgainAndShuffle();
  };

  const speakInstructionManually = () => {
    speakText(`Hello ${activePlayer}! Count the items in each row and click the button with the matching count.`);
  };

  return (
    <div style={styles.container}>
      <main style={styles.main}>
        {/* Controls Toolbar */}
        <section style={styles.toolbar}>
          {/* Top Left Toolbar Group */}
          <div style={styles.toolbarGroupLeft}>
            {/* Player Input */}
            <div style={styles.inputGroup}>
              <User style={{ width: 18, height: 18, color: '#3B82F6', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Enter Name"
                value={playerName}
                onChange={handleNameInputChange}
                style={styles.input}
              />
            </div>

            {/* Voice Audio Controls Group */}
            <div style={styles.audioControls}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={speakInstructionManually}
                style={{
                  ...styles.voiceBtn,
                  opacity: isMuted ? 0.6 : 1
                }}
                title="Listen to Instructions"
              >
                <Volume2 style={{ width: 18, height: 18 }} />
                <span>Listen</span>
              </motion.button>

              {/* Mute / Unmute Toggle */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleMute}
                style={{
                  ...styles.muteBtn,
                  background: isMuted ? '#FEE2E2' : '#F1F5F9',
                  color: isMuted ? '#EF4444' : '#64748B'
                }}
                title={isMuted ? "Unmute Voice" : "Mute Voice"}
              >
                {isMuted ? <VolumeX style={{ width: 18, height: 18 }} /> : <Volume2 style={{ width: 18, height: 18 }} />}
              </motion.button>
            </div>
          </div>

          {/* Top Right Toolbar Group */}
          <div style={styles.toolbarGroupRight}>
            {/* Play Again (Shuffle) Button */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handlePlayAgainAndShuffle}
              style={styles.playBtn}
            >
              <Play style={{ width: 16, height: 16 }} />
              <span>Play Again</span>
            </motion.button>

            {/* Timer Display */}
            <div style={styles.timerGroup}>
              <label style={styles.timerLabel}>
                <input
                  type="checkbox"
                  checked={isTimerActive}
                  onChange={(e) => setIsTimerActive(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Timer
              </label>
              <div style={styles.timerText}>
                <Clock style={{ width: 16, height: 16 }} />
                <span>{timer}s</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Game Stage - Clean White Card */}
        <section style={styles.gameCardWhite}>
          <div style={styles.gameHeader}>
            <h2 style={styles.gameTitle}>
              <Sparkles style={{ width: 22, height: 22, color: '#F59E0B', flexShrink: 0 }} />
              <span>Count & Match Numbers</span>
            </h2>
            <span style={styles.playerBadge}>
              Player: {activePlayer}
            </span>
          </div>

          {/* Questions Rows */}
          <div style={styles.rowsContainer}>
            {objectsList.map((obj) => (
              <div key={obj.id} style={styles.objectRow}>
                {/* Visual Emojis */}
                <div style={styles.emojiContainer}>
                  {Array.from({ length: obj.count }).map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: i * 0.03 }}
                      style={styles.emoji}
                      title={obj.name}
                    >
                      {obj.emoji}
                    </motion.span>
                  ))}
                </div>

                {/* Number Choices Buttons */}
                <div style={styles.numberContainer}>
                  {numberOptions.map((num) => {
                    const isSelected = selectedAnswers[obj.id] === num;
                    return (
                      <motion.button
                        key={num}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleSelectNumber(obj.id, num)}
                        style={{
                          ...styles.numBtn,
                          ...(isSelected ? styles.numBtnSelected : styles.numBtnUnselected)
                        }}
                      >
                        {num}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Action Bar */}
          <div style={styles.actionBar}>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSubmit}
              style={styles.submitBtn}
            >
              Submit Answer
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleResetCurrent}
              style={styles.resetBtn}
            >
              <RefreshCw style={{ width: 18, height: 18 }} />
              <span>Clear Choices</span>
            </motion.button>
          </div>
        </section>
      </main>

      {/* Modal / Popup Overlay */}
      <AnimatePresence>
        {showModal && (
          <div style={styles.modalOverlay}>
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              style={styles.modalCard}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                {gameResult.ok ? (
                  <div style={styles.iconCircleSuccess}>
                    <Trophy style={{ width: 40, height: 40, color: '#10B981' }} />
                  </div>
                ) : (
                  <div style={styles.iconCircleFail}>
                    <XCircle style={{ width: 40, height: 40, color: '#EF4444' }} />
                  </div>
                )}
              </div>

              {gameResult.ok && (
                <div style={{ color: '#F59E0B', fontSize: '1.4rem', marginBottom: 8, letterSpacing: '2px' }}>
                  ⭐⭐⭐⭐⭐
                </div>
              )}

              <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#1E293B', marginBottom: 8 }}>
                {gameResult.ok ? `Great job, ${activePlayer}!` : `Try Again, ${activePlayer}!`}
              </h3>

              <p style={{ color: '#64748B', fontSize: '0.95rem', marginBottom: 16, lineHeight: 1.4 }}>
                {gameResult.message}
              </p>

              <div style={styles.modalTimerBox}>
                <Clock style={{ width: 16, height: 16, color: '#2563EB' }} /> Time Spent: <strong>{timer}s</strong>
              </div>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                onClick={closeModal}
                style={styles.modalCloseBtn}
              >
                Shuffle & Play Again
              </motion.button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#F1F5F9',
    color: '#0F172A',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: 'clamp(12px, 3vw, 32px) clamp(8px, 2vw, 16px)',
    boxSizing: 'border-box'
  },
  main: {
    maxWidth: '880px',
    margin: '0 auto',
    width: '100%'
  },
  toolbar: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '16px',
    padding: '12px 16px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  toolbarGroupLeft: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px',
    flex: '1 1 auto'
  },
  toolbarGroupRight: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px'
  },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '8px 12px',
    border: '1px solid #CBD5E1',
    flex: '1 1 140px',
    minWidth: '130px'
  },
  input: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#0F172A',
    fontSize: '0.9rem',
    fontWeight: '500',
    width: '100%'
  },
  audioControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  voiceBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    color: '#2563EB',
    padding: '8px 12px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.85rem',
    cursor: 'pointer'
  },
  muteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 10px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    cursor: 'pointer'
  },
  playBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    padding: '8px 16px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '0.88rem',
    cursor: 'pointer'
  },
  timerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#F8FAFC',
    borderRadius: '10px',
    padding: '7px 12px',
    border: '1px solid #CBD5E1'
  },
  timerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#475569',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  timerText: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#2563EB',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    fontSize: '0.9rem'
  },
  gameCardWhite: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: 'clamp(16px, 3vw, 28px)',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)'
  },
  gameHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '2px solid #F1F5F9',
    paddingBottom: '12px',
    marginBottom: '16px',
    gap: '8px'
  },
  gameTitle: {
    fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  playerBadge: {
    fontSize: '0.8rem',
    fontWeight: '600',
    background: '#EFF6FF',
    color: '#2563EB',
    border: '1px solid #BFDBFE',
    padding: '4px 12px',
    borderRadius: '20px'
  },
  rowsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  objectRow: {
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '14px',
    padding: 'clamp(12px, 2vw, 16px)',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px'
  },
  emojiContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 'clamp(4px, 1vw, 8px)',
    minHeight: '38px',
    alignItems: 'center',
    flex: '1 1 220px'
  },
  emoji: {
    fontSize: 'clamp(1.5rem, 3.5vw, 1.85rem)',
    userSelect: 'none'
  },
  numberContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '0 0 auto',
    width: '100%',
    maxWidth: '360px',
    margin: '0 auto'
  },
  numBtn: {
    minWidth: '44px',
    height: '44px',
    padding: '0 12px',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    touchAction: 'manipulation',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease'
  },
  numBtnUnselected: {
    background: '#FFFFFF',
    color: '#334155',
    border: '1px solid #CBD5E1'
  },
  numBtnSelected: {
    background: '#2563EB',
    color: '#FFFFFF',
    border: '1px solid #1D4ED8',
    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
  },
  actionBar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '2px solid #F1F5F9'
  },
  submitBtn: {
    padding: '12px 28px',
    background: '#059669',
    border: 'none',
    borderRadius: '12px',
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: '0.95rem',
    cursor: 'pointer',
    flex: '1 1 180px',
    maxWidth: '260px'
  },
  resetBtn: {
    padding: '12px 22px',
    background: '#F1F5F9',
    border: '1px solid #CBD5E1',
    borderRadius: '12px',
    color: '#475569',
    fontWeight: '600',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    flex: '1 1 160px',
    maxWidth: '220px'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)'
  },
  modalCard: {
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '360px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
  },
  iconCircleSuccess: {
    padding: '10px',
    background: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: '50%'
  },
  iconCircleFail: {
    padding: '10px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '50%'
  },
  modalTimerBox: {
    background: '#F8FAFC',
    borderRadius: '8px',
    padding: '8px 16px',
    marginBottom: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#334155',
    border: '1px solid #E2E8F0',
    fontSize: '0.88rem'
  },
  modalCloseBtn: {
    width: '100%',
    padding: '12px',
    background: '#2563EB',
    border: 'none',
    color: '#FFFFFF',
    fontWeight: '700',
    borderRadius: '10px',
    cursor: 'pointer'
  }
};